import type { ReactNode } from 'react'
import type { P4InitiativeStage, P4Objective, P4StrategyVersion, P4UseCase } from './p4-api'

export function PageHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <header className="page-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action ? <div className="page-header-action">{action}</div> : null}
    </header>
  )
}

export function ErrorAlert({ error }: { error: unknown }) {
  if (!error) return null
  return (
    <div className="inline-alert danger" role="alert">
      {error instanceof Error ? error.message : 'Something went wrong.'}
    </div>
  )
}

export function PortfolioAccessMessage({ area }: { area: string }) {
  return (
    <div className="page-stack p4-page">
      <PageHeader
        eyebrow={area}
        title={`You do not have organisation-wide ${area.toLowerCase()} access`}
        description="Your Swell role determines which organisation-wide views you can open."
      />
      <section className="panel">
        <p>
          You can continue working with AI uses available to your role in the AI Register. If you need organisation-wide {area.toLowerCase()} access, ask your organisation administrator to review your role.
        </p>
      </section>
    </div>
  )
}

export function dateLabel(value: string | null) {
  if (!value) return 'Not set'
  const date = new Date(`${value}T00:00:00`)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

export const initiativeStageLabels: Record<P4InitiativeStage, string> = {
  idea: 'Opportunity',
  under_consideration: 'Exploring',
  prioritised: 'Prioritised',
  planned: 'Planned',
  in_progress: 'In progress',
  on_hold: 'On hold',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  archived: 'Archived',
}

export const initiativeStages = Object.keys(initiativeStageLabels) as P4InitiativeStage[]

export const allowedNextStages: Record<P4InitiativeStage, P4InitiativeStage[]> = {
  idea: ['idea', 'under_consideration', 'cancelled'],
  under_consideration: ['under_consideration', 'prioritised', 'cancelled'],
  prioritised: ['prioritised', 'planned', 'cancelled'],
  planned: ['planned', 'in_progress', 'on_hold', 'cancelled'],
  in_progress: ['in_progress', 'delivered', 'on_hold', 'cancelled'],
  on_hold: ['on_hold', 'planned', 'in_progress', 'cancelled'],
  delivered: ['delivered', 'archived'],
  cancelled: ['cancelled', 'archived'],
  archived: ['archived'],
}

export const operationalStateLabels: Record<P4UseCase['operationalState'], string> = {
  proposed: 'Proposed',
  experiment: 'Experiment',
  pilot: 'Pilot',
  production: 'Production',
  suspended: 'Suspended',
  retired: 'Retired',
}

export function currentObjectives(versions: P4StrategyVersion[]): P4Objective[] {
  return versions.find((version) => version.status === 'current')?.objectives.filter((objective) => objective.status === 'active') ?? []
}

export function toggleId(ids: string[], id: string) {
  return ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id]
}
